/* Easonergy uses native Shopify forms and hosted checkout. No credentials or procurement data belong here. */
(() => {
  'use strict';
  const divisions = new Set(['style', 'finds', 'tech', 'home']);
  function literalTerms(value) {
    return value.normalize('NFKC').replace(/[^\p{L}\p{N}\s'-]/gu, ' ').trim().split(/\s+/).filter(Boolean).slice(0, 20);
  }
  const audiences = new Set(['men', 'women', 'teens', 'children']);
  function searchScope(division, audience = 'all', originals = false) {
    if (division === 'style' && originals) audience = 'men';
    return `tag:"division:${division}"` + (division === 'style' && audiences.has(audience) ? ` AND tag:"audience-${audience}"` : '') + (division === 'style' && originals ? ' AND tag:"brand-easonergy-originals"' : '');
  }
  function scopedQuery(division, value, audience = 'all', originals = false) {
    const terms = literalTerms(value);
    return searchScope(division, audience, originals) + (terms.length ? ' AND ' + terms.map(term => `"${term}"`).join(' AND ') : '');
  }
  if (document.querySelector('[data-rescope-search]')) {
    const target = new URL(location.href);
    const form = document.querySelector('[data-scoped-search]');
    const division = divisions.has(form?.dataset.division) ? form.dataset.division : 'style';
    const audience = audiences.has(form?.dataset.audience) ? form.dataset.audience : 'all';
    const originals = form?.dataset.originals === 'true';
    const keywords = (target.searchParams.get('q') || '').replace(/tag:"(?:division:(?:style|finds|tech|home)|audience-(?:men|women|teens|children)|brand-easonergy-originals)"/g, '').replace(/\bAND\b/g, ' ');
    target.searchParams.set('q', scopedQuery(division, keywords, audience, originals));
    target.searchParams.set('type', 'product');
    target.searchParams.delete('page');
    // A normalized query may still be rendered by Shopify's fallback branch.
    // Never reload an identical URL; keep the search form usable instead.
    if (target.toString() !== location.href) {
      location.replace(target.toString());
      return;
    }
  }
  function initialize(root = document) {
    root.querySelectorAll('[data-scoped-search]').forEach(form => {
      if (form.dataset.initialized) return;
      form.dataset.initialized = 'true';
      const division = divisions.has(form.dataset.division) ? form.dataset.division : 'style';
      const audience = audiences.has(form.dataset.audience) ? form.dataset.audience : 'all';
      const originals = form.dataset.originals === 'true';
      const input = form.querySelector('[data-search-text]');
      const query = form.querySelector('[data-query]');
      const scope = searchScope(division, audience, originals);
      const current = new URLSearchParams(location.search).get('q') || '';
      if (location.pathname.endsWith('/search') && current.startsWith(scope)) {
        input.value = current.slice(scope.length).replace(/^\s+AND\s+/, '').replace(/\s+AND\s+/g, ' ').replace(/"/g, '').trim();
      }
      form.addEventListener('submit', () => {
        // Treat user keywords as literal terms; syntax cannot remove the division constraint.
        query.value = scopedQuery(division, input.value, audience, originals);
      });
    });
    root.querySelectorAll('[data-product-context]').forEach(page => {
      if (page.dataset.contextInitialized) return;
      page.dataset.contextInitialized = 'true';
      let productTags = [];
      try { productTags = JSON.parse(page.dataset.productTags || '[]'); } catch (_) { return; }
      const requested = new URLSearchParams(location.search).get('ec');
      const allowedCategories = new Set(['t-shirts', 'pants', 'shoes', 'hats', 'watches', 'accessories', 'desk-toys', 'fidgets', 'puzzles', 'gifts', 'lighting', 'audio', 'charging', 'phone-accessories', 'smart-home', 'desk', 'storage', 'kitchen', 'cleaning', 'bathroom', 'laundry', 'desk-charging', 'home-helpers', 'garden-watering'].map(value => `category-${value}`));
      const category = allowedCategories.has(requested) && productTags.includes(requested) ? requested : page.dataset.contextCategory;
      const label = category === 'category-t-shirts' ? 'T-shirts' : (category || '').replace(/^category-/, '').replaceAll('-', ' ').replace(/^./, value => value.toUpperCase());
      page.querySelectorAll('[data-context-category-label]').forEach(node => { node.textContent = label; });
      const back = page.querySelector('[data-context-back]');
      if (back && allowedCategories.has(category)) {
        const url = new URL(back.href, location.origin);
        url.pathname = url.pathname.replace(/\/category-[^/]+$/, '') + '/' + category;
        back.href = url.pathname;
      }
      document.querySelectorAll('.category-nav a').forEach(link => {
        if (new URL(link.href).pathname.endsWith('/' + category)) link.setAttribute('aria-current', 'page');
        else link.removeAttribute('aria-current');
      });
      const related = page.querySelector('[data-contextual-related]');
      if (!related) return;
      let shown = 0;
      related.querySelectorAll('[data-related-card]').forEach(card => {
        let tags = [];
        try { tags = JSON.parse(card.dataset.productTags || '[]'); } catch (_) { /* Hide malformed context. */ }
        const match = allowedCategories.has(category) && tags.includes(category);
        card.hidden = !match || shown >= 4;
        if (!card.hidden) shown += 1;
        card.querySelectorAll('[data-context-product-link]').forEach(link => {
          const url = new URL(link.href, location.origin);
          if (allowedCategories.has(category)) url.searchParams.set('ec', category);
          else url.searchParams.delete('ec');
          link.href = url.pathname + url.search;
        });
      });
      related.hidden = shown === 0;
      const audience = related.dataset.audience;
      const suffix = audiences.has(audience) ? ' · ' + audience[0].toUpperCase() + audience.slice(1) : '';
      related.querySelector('[data-related-title]').textContent = 'More in ' + label + suffix + (related.dataset.originals === 'true' ? ' · Originals' : '');
      related.querySelector('[data-related-link]').href = related.dataset.collectionUrl + (allowedCategories.has(category) ? '/' + category : '');
    });
    root.querySelectorAll('[data-country-select]').forEach(select => {
      if (select.dataset.initialized) return;
      select.dataset.initialized = 'true';
      select.value = select.dataset.default;
      const province = document.getElementById(select.dataset.provinceId);
      const update = () => {
        const selected = select.selectedOptions[0];
        let values = [];
        try { values = JSON.parse(selected?.dataset.provinces || '[]'); } catch (_) { values = []; }
        province.replaceChildren(new Option('Select state / province', ''));
        values.forEach(([value, label]) => province.add(new Option(label, value)));
        province.value = province.dataset.default || '';
        province.required = values.length > 0;
      };
      select.addEventListener('change', update);
      update();
    });
    root.querySelectorAll('[data-variant-select]').forEach(select => {
      if (select.dataset.initialized) return;
      select.dataset.initialized = 'true';
      select.addEventListener('change', () => {
        const option = select.selectedOptions[0];
        const form = select.closest('form');
        const button = form.querySelector('[data-add-button]');
        const available = option.dataset.available === 'true';
        button.disabled = !available;
        button.textContent = available ? 'Add to bag' : 'Unavailable';
        const price = form.closest('.product-details').querySelector('[data-product-price]');
        if (price) price.textContent = option.dataset.price;
        const url = new URL(location.href);
        url.searchParams.set('variant', option.value);
        if (location.protocol === 'https:' || location.protocol === 'http:') history.replaceState({}, '', url);
      });
    });
  }
  initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
})();
